import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  input,
  InputSignal,
  OnInit,
  Output,
  Signal
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { convertToHttps } from '@k12/app-services';
import { ComponentType, InputType, PromptComponent } from '@k12/models';

interface AgreementLink {
  terms?: string;
  privacy?: string;
}

interface LabelPart {
  href?: string;
  text: string;
}

interface LabelMatch {
  href?: string;
  index: number;
  text: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatCheckboxModule, ReactiveFormsModule],
  selector: 'lib-pc-terms-and-conditions',
  styleUrl: './pc-terms-and-conditions.component.scss',
  templateUrl: './pc-terms-and-conditions.component.html'
})
export class PcTermsAndConditionsComponent implements OnInit {
  public allowPointerEvents: InputSignal<boolean | undefined> = input();
  public configJson: InputSignal<PromptComponent> = input<PromptComponent>({
    componentType: ComponentType.TermsAndConditions,
    header: '',
    inputType: InputType.Checkbox,
    instructions: '',
    label: '',
    order: 0,
    inputs: [
      {
        dataType: InputType.Checkbox,
        inputType: InputType.Checkbox,
        instructions: '',
        isRequired: true,
        label: 'I agree to the Terms & Conditions and Privacy Policy of NCSEAA',
        order: 0,
        options: [],
        link: {}
      }
    ]
  });
  public enrollmentBuilderView = input<boolean>(false);
  public formGroup: InputSignal<FormGroup | undefined> = input<FormGroup | undefined>();

  // This computed signal will return the formGroup if it is provided, 
  // otherwise it will return a fallback form group with a single control for the admin form name.
  public readonly resolvedFormGroup: Signal<FormGroup> = computed(() => this.formGroup() ?? this.fallbackFormGroup);
  public readonly labelParts: Signal<LabelPart[][]> = computed(() => {
    const cfg = this.configJson();
    const inputs = cfg?.inputs ?? [];
    return inputs.map((inputItem) => this.buildLabelParts(inputItem.label, inputItem.link));
  });

  private readonly fallbackFormGroup: FormGroup = new FormGroup({
    adminFormName: new FormControl(false, Validators.required),
  });

  @Output() cbConfig = new EventEmitter();
  @Output() checkboxSelect = new EventEmitter();

  public ngOnInit(): void {
    this.cbConfig.emit(this.configJson());
  }

  // This method retrieves the FormControl for the checkbox based on the provided control name.
  public getCheckboxControl(controlName: string = 'adminFormName'): FormControl<boolean | null> {
    const name = controlName;
    const formGroup = this.resolvedFormGroup();
    const existingControl = formGroup.get(name);

    if (existingControl instanceof FormControl) {
      return existingControl as FormControl<boolean | null>;
    }

    const control = new FormControl<boolean | null>(false, name === 'adminFormName' ? Validators.required : null);
    formGroup.addControl(name, control);
    return control;
  }

  // This method is called when the checkbox is clicked. 
  // It emits an event with the form name and the current state of the checkbox.
  public checkboxClick(checked: boolean): void {
    this.checkboxSelect.emit({
      formName: this.configJson().id ?? '',
      control: { [this.configJson().inputs[0].id ?? '']: checked }
    });
  }

  private buildLabelParts(label?: string, link?: AgreementLink): LabelPart[] {
    const termsText = 'Terms & Conditions';
    const privacyText = 'Privacy Policy';
    const termsTextLower = termsText.toLocaleLowerCase();
    const privacyTextLower = privacyText.toLocaleLowerCase();
    const parts: LabelPart[] = [];

    if (!label) return parts;

    const labelLower = label.toLocaleLowerCase();

    const termsHref = link?.terms ? convertToHttps(link.terms) : undefined;
    const privacyHref = link?.privacy ? convertToHttps(link.privacy) : undefined;

    let cursor = 0;
    // Loop through the label string and find matches for the terms and privacy text, 
    // creating LabelPart objects for each segment.
    while (cursor < label.length) {
      const match = this.findNextLabelMatch(labelLower, cursor, termsTextLower, privacyTextLower, termsHref, privacyHref);
      if (!match) {
        parts.push({ text: label.slice(cursor) });
        break;
      }

      // If there is text before the match, add it as a LabelPart.
      if (match.index > cursor) {
        parts.push({ text: label.slice(cursor, match.index) });
      }

      // Add the matched text as a LabelPart, including the href if it exists.
      const matchedText = label.slice(match.index, match.index + match.text.length);
      if (match.href) {
        parts.push({ href: match.href, text: matchedText });
      } else {
        parts.push({ text: matchedText });
      }

      cursor = match.index + match.text.length;
    }
    return parts;
  }

  // This method finds the next occurrence of either the terms or privacy text in the label string, starting from the given cursor position.
  private findNextLabelMatch(
    label: string,
    cursor: number,
    termsText: string,
    privacyText: string,
    termsHref?: string,
    privacyHref?: string
  ): LabelMatch | null {
    const termsIndex = label.indexOf(termsText, cursor);
    const privacyIndex = label.indexOf(privacyText, cursor);

    if (termsIndex === -1 && privacyIndex === -1) {
      return null;
    }

    if (termsIndex !== -1 && (privacyIndex === -1 || termsIndex < privacyIndex)) {
      return { href: termsHref, index: termsIndex, text: termsText };
    }

    return { href: privacyHref, index: privacyIndex, text: privacyText };
  }
}